import type {Postscriptum} from "../dist/d.ts/postscriptum";
import type {Columnizator} from "../dist/d.ts/columns";

/**
 * Plugin that automatically adjusts the height of column content to optimize layout.
 * The resizing behavior is described in the `-psp-ach-steps` property.
 *
 * Syntax :
 * ```
 * -psp-ach-steps =
 *   <steps>#
 *
 * <steps> =
 *   <keyframes-name> <steps> [ / <break-type> ]?
 *
 * <keyframes-name> =
 *   <custom-ident>
 *
 * <steps> =
 *   <number [1,∞]>
 *
 * <break-type> = all | box | inline
 * ```
 *
 * Exemple :
 * ```
 * #container {
 *   columns: 2;
 *   --psp-ach-steps: growLineHeight 5 / box, growMargins 5;
 * }
 *
 * p {
 *   margin-block: var(--blockMargin);
 * }
 *
 * @keyframes growLineHeight {
 *   from {
 *     line-height: 1.15;
 *   }
 *   to {
 *     line-height: 1.2;
 *   }
 * }
 *
 * @keyframes growMargins {
 *   to {
 *     --blockMargin: 1.2em;
 *   }
 * }
 * ```
 */
(postscriptum as Postscriptum).plugin<void>('ps-adjust-columns-height', (processor: Columnizator) => {
	const {css, util, mutations} = postscriptum;
	const {logger, rects} = util;

	CSS.registerProperty({
		name: '--psp-ach-steps',
		syntax: '*',
		initialValue: 'none',
		inherits: false
	});


	processor.on("columnization-end", function (this: Columnizator) {
		// Can be empty if there is only one span
		if (!this.fragments.length) return;
		if (this.source.closest('[psp-sl-layout-test]')) return;
		if (this.lastEvent == 'no-break-point') return;
		const sourceStyle = getComputedStyle(this.source);

		const stepsPropList = css.getCustomPluginProp(sourceStyle, 'ach-steps');
		if (stepsPropList == "none") return;
		const stepCounts = [];
		const breakTypes = [];
		const animsList = stepsPropList.split(',').map((stepProp) => {
			const [ anim, params ] = stepProp.trim().split('/');
			const [keyframes, stepsCount, timing] = anim.trim().split(' ');
			stepCounts.push(parseInt(stepsCount));
			breakTypes.push(params?.trim() || 'all');
			return `${stepsCount}s ${timing || 'linear'} paused ${keyframes}`;
		});
		const animsProp = animsList.join(',');

		const range = document.createRange();
		let maxBottom = -Infinity;
		let maxBottomColumn = null;
		const bottoms = [];
		for (let iCol = 0; iCol < this.columnCount; iCol++) {
			const column = this.fragments[iCol];
			if (column.body?.hasAttribute('ps-breaked-after')) {
				range.selectNodeContents(column.body);
				const contentBottom = rects.boundingScrollRect(range).bottom;
				bottoms[iCol] = contentBottom;
				if (contentBottom > maxBottom) {
					maxBottom = contentBottom;
					maxBottomColumn = column;
				}
			}
		}

		for (let iCol = 0; iCol < this.columnCount; iCol++) {
			const column = this.fragments[iCol];
			if (column.body && column != maxBottomColumn) {
				logger.addScope(`column ${column.number}`);
				const animMutations = new mutations.Mutations();
				animMutations.setStyle(column.body, {animation: animsProp});
				const anims = column.body.getAnimations() as CSSAnimation[];
				if (anims.length != animsList.length) {
					logger.warn(`Unable to define the animation 'animsProp'. Are some keyframes at-rules missing ?`);
					return;
				}
				const allBreaked = column.body.querySelectorAll<HTMLElement>('[ps-breaked-after]');
				const colBreakType = allBreaked.item(allBreaked.length - 1)?.style.textAlignLast ? 'inline' : 'box';
				const resultSteps = [];
				for (let i = 0; i < anims.length; i++) {
					const anim = anims[i];
					const breakType = breakTypes[i];
					if (breakType != 'all' && breakType != colBreakType) {
						anim.cancel();
						resultSteps.push(0);
						continue;
					}

					const steps = stepCounts[i];
					range.selectNodeContents(column.body);
					let lastStep = 0;
					let lastBottom = -Infinity;
					for (let step = 1; step <= steps; step++) {
						anim.currentTime = step * 1000 - 1;
						const bottom = rects.boundingScrollRect(range).bottom;
						if (bottom > maxBottom) break;
						lastStep = step;
						lastBottom = bottom;
					}
					if (lastStep && lastBottom > bottoms[iCol]) {
						anim.currentTime = lastStep * 1000 - 1;
						resultSteps.push(lastStep);
					} else {
						anim.cancel();
						resultSteps.push(0);
					}
				}
				const adjusted = resultSteps.filter((rs) => rs);
				if (adjusted.length) {
					this.layoutContext.mutations.setAnimations(column.body);
					logger.info("Column ajusted: " + anims.map((a, i) => `${a.animationName} ${resultSteps[i]}/${stepCounts[i]}`).join(', '));
				}
				animMutations.revert();
				logger.removeScope();
			}
		}

	}, {inherited: true});
});
